/**
 * 발자취 — 여행이 끝난 뒤 하루를 한 장으로.
 *
 * 지도로 모양을, 타임라인으로 시간을 보여 줍니다. 둘 중 하나만으로는
 * 부족합니다 — 지도만 보면 언제 얼마나 있었는지 모르고, 목록만 보면
 * 어떻게 움직였는지 모릅니다.
 *
 * 타임라인의 핵심은 **머문 시간**입니다. 오른쪽 끝에 모아 줄마다 자리를
 * 맞췄습니다 — 그래야 세로로 훑으며 어디서 오래 있었는지 보입니다.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Section, SectionHeader, Tabs } from 'components/ds';
import { MapContainer } from 'components/map/MapContainer';
import { IcCamera, IcClock, IcRun, IcTaxi } from 'components/common/icons/EmojiIcons';
import type { TraceDay, TraceSegment } from 'hooks/useMeetingTrace';
import styles from './TraceHistorySection.module.css';

interface Props {
    days: TraceDay[];
    /** 일차별 사진·글 수 — 기록과 이어집니다 */
    noteCountBySub?: Record<number, { photos: number; notes: number }>;
}

/** 초 → "1시간 50분" / "32분" */
const fmtDuration = (sec?: number | null, t?: (k: string, o?: any) => string): string => {
    if (!sec || sec <= 0) return '';
    const m = Math.round(sec / 60);
    if (m < 60) return t ? t('trace.minutesOnly', { n: m }) : `${m}분`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest === 0
        ? (t ? t('trace.hoursOnly', { n: h }) : `${h}시간`)
        : (t ? t('trace.hoursMinutes', { h, m: rest }) : `${h}시간 ${rest}분`);
};

const fmtDistance = (m?: number | null): string => {
    if (!m || m <= 0) return '';
    return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${Math.round(m)}m`;
};

const fmtClock = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/* 이동 수단별 아이콘 — 라벨과 모양이 어긋나면 안 읽힙니다.
   (자전거 아이콘이 따로 없어 걷기와 같은 IcRun 을 씁니다 — 라벨이 가릅니다) */
const MOVE_ICON: Record<string, typeof IcRun> = {
    W: IcRun,
    B: IcRun,
    C: IcTaxi,
};

export const TraceHistorySection: React.FC<Props> = ({ days, noteCountBySub }) => {
    const { t } = useTranslation();
    const [activeIdx, setActiveIdx] = useState(0);

    const day = days[activeIdx] || days[0];

    /* 지도 — 체류 지점에 번호를 찍고 순서대로 잇습니다 */
    const { markers, routes, center } = useMemo(() => {
        const pts = (day?.stays || []).filter(
            s => typeof s.latitude === 'number' && typeof s.longitude === 'number');
        const mk = pts.map((s, i) => ({
            id: `trace-${s.traceId}`,
            lat: s.latitude as number,
            lng: s.longitude as number,
            label: String(i + 1),
            tooltip: s.placeName || '',
        }));
        const coords = pts.map(s => [s.latitude as number, s.longitude as number] as [number, number]);
        return {
            markers: mk,
            routes: coords.length >= 2
                ? [{ coordinates: coords, color: '#1f6f78', strokeWidth: 4 }]
                : [],
            center: pts.length > 0
                ? { lat: pts[0].latitude as number, lng: pts[0].longitude as number }
                : null,
        };
    }, [day]);

    if (days.length === 0) return null;

    return (
        <Section divided>
            <SectionHeader title={t('trace.historyTitle')} sub={t('trace.historySub')} />

            {days.length > 1 && (
                <div className={styles.tabs}>
                    <Tabs
                        items={days.map((d, i) => ({
                            value: String(i),
                            label: d.day ? `Day ${d.day}` : t('trace.noDay'),
                        }))}
                        value={String(activeIdx)}
                        onChange={(v) => setActiveIdx(Number(v))}
                        ariaLabel={t('trace.dayAria')}
                    />
                </div>
            )}

            {center && (
                <div className={styles.map}>
                    <MapContainer
                        center={center}
                        markers={markers}
                        routes={routes}
                        height={260}
                        fitBounds={markers.length > 1}
                    />
                </div>
            )}

            {/* 그날을 한 줄로 — 지도만 보면 규모가 안 잡힙니다 */}
            <div className={styles.summary}>
                <span>{t('trace.placeCount', { n: day.placeCount })}</span>
                {day.distanceM > 0 && <span>{fmtDistance(day.distanceM)} {t('trace.moved')}</span>}
                {day.moveSec > 0 && <span>{t('trace.moveTime')} {fmtDuration(day.moveSec, t)}</span>}
            </div>

            <ul className={styles.list}>
                {day.stays.map((s, i) => (
                    <StayRow
                        key={s.traceId}
                        stay={s}
                        index={i}
                        move={day.moves[i] || null}
                        counts={s.subId && noteCountBySub ? noteCountBySub[s.subId] : undefined}
                    />
                ))}
            </ul>
        </Section>
    );
};

const StayRow: React.FC<{
    stay: TraceSegment;
    index: number;
    move: TraceSegment | null;
    counts?: { photos: number; notes: number };
}> = ({ stay, index, move, counts }) => {
    const { t } = useTranslation();
    return (
        <li className={styles.row}>
            <span className={styles.rail}>
                <span className={styles.dot}>{index + 1}</span>
                {move && <span className={styles.line} />}
            </span>

            <span className={styles.body}>
                <span className={styles.head}>
                    <b className={styles.time}>{fmtClock(stay.startedAt)}</b>
                    <span className={styles.place}>{stay.placeName || ''}</span>
                    {/* 계획에 없이 들른 곳 — 다음 일정을 고칠 재료입니다 */}
                    {!stay.subId && <Badge tone="outline">{t('trace.offPlan')}</Badge>}
                    <span className={styles.stay}>{fmtDuration(stay.durationSec, t)}</span>
                </span>

                {counts && (counts.photos > 0 || counts.notes > 0) && (
                    <span className={styles.meta}>
                        {counts.photos > 0 && (
                            <span className={styles.metaItem}><IcCamera size={12} />{counts.photos}</span>
                        )}
                        {counts.notes > 0 && <span>{t('trace.noteCount', { n: counts.notes })}</span>}
                    </span>
                )}

                {/* 이동은 두 장소 '사이' 에 놓아 무엇과 무엇 사이인지 분명히 합니다 */}
                {move && (
                    <span className={styles.move}>
                        {(() => {
                            const MoveIc = MOVE_ICON[move.moveMode || ''] || IcClock;
                            return <MoveIc size={12} />;
                        })()}
                        <span>
                            {t(`trace.mode${move.moveMode || 'U'}`)} {fmtDuration(move.durationSec, t)}
                            {move.distanceM ? ` · ${fmtDistance(move.distanceM)}` : ''}
                        </span>
                    </span>
                )}
            </span>
        </li>
    );
};
